import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import './nested-categories.scss';
import { CategoryTreeItem } from '@zonofi/zono-money-store';
import { SVGS } from '@zonofi/zono-money-design-system';
import { useSearchParams } from 'react-router-dom';
import { CategoryButton } from '../category-button/category-button';

interface NestedCategoriesProps {
  categories: CategoryTreeItem;
  updateSelectedCategories: (val: CategoryTreeItem[]) => void;
}

interface RenderCategoriesProps {
  categories: CategoryTreeItem[];
  handleCategoryClick: (category: CategoryTreeItem) => void;
  selectedCategories: CategoryTreeItem[];
}

const RenderCategories: React.FC<RenderCategoriesProps> = ({
  categories,
  handleCategoryClick,
  selectedCategories,
}) => {
  return categories?.map((category, id) => (
    <CategoryButton
      key={id}
      item={category}
      className={`category-button ${
        selectedCategories?.some(
          (c) => c?.categoryCode === category?.categoryCode
        )
          ? 'selected'
          : ''
      }`}
      onClick={() => handleCategoryClick(category)}
      buttonName={category?.name || ''}
      hasChildren={category?.children && category?.children?.length > 0}
    />
  ));
};
export const NestedCategories: React.FC<NestedCategoriesProps> = ({
  categories,
  updateSelectedCategories,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryTreeItem[]
  >([]);
  const [activeParent, setActiveParent] = useState<CategoryTreeItem | null>(
    null
  );

  const upsertParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    setSearchParams(params);
  };

  const deleteParams = (...keys: string[]) => {
    const params = new URLSearchParams(searchParams);

    keys.forEach((key) => params.delete(key));

    setSearchParams(params);
  };

  const handleCategoryClick = useCallback(
    (category: CategoryTreeItem) => {
      const isParent = category?.children && category?.children.length > 0;

      // --------------------------
      // CASE 1: PARENT CATEGORY CLICKED (expand)
      // --------------------------
      if (isParent) {
        setActiveParent(category);
        setSelectedCategories([]);
        updateSelectedCategories([category]);

        // set only categoryId (parent)
        upsertParam('pCategoryId', category.categoryCode);
        return;
      }

      // --------------------------
      // CASE 2: LEAF CATEGORY CLICKED
      // Toggle behavior:
      // --------------------------
      setSelectedCategories((prev) => {
        const alreadySelected = prev.some((c) => c.id === category.id);

        // ---- Deselection (remove leaf) ----
        if (alreadySelected) {
          // If user deselects leaf, fallback to parent as selection
          if (activeParent) {
            updateSelectedCategories([activeParent]);
            upsertParam('categoryId', activeParent.categoryCode);
            deleteParams('pCategoryId');
          } else {
            updateSelectedCategories([]);
            deleteParams('categoryId', 'pCategoryId');
          }

          return [];
        }

        // ---- Selection (select leaf) ----
        const newSelection = [category];

        updateSelectedCategories(newSelection);

        if (activeParent) {
          upsertParam('pCategoryId', activeParent.categoryCode);
        }
        upsertParam('categoryId', category.categoryCode);

        return newSelection;
      });
    },
    [activeParent, updateSelectedCategories, searchParams]
  );

  const findCategoryByCode = (
    category: CategoryTreeItem,
    code: string
  ): CategoryTreeItem | null => {
    if (category.categoryCode === code) return category;
    for (const child of category?.children || []) {
      const result = findCategoryByCode(child, code);
      if (result) return result;
    }
    return null;
  };

  const findParentCategory = (
    category: CategoryTreeItem,
    parentId: string
  ): CategoryTreeItem | null => {
    if (category.id === parentId) return category;
    for (const child of category?.children || []) {
      const result = findParentCategory(child, parentId);
      if (result) return result;
    }
    return null;
  };

  const onClickBack = () => {
    if (activeParent?.parentId) {
      const parentCategory = findParentCategory(
        categories,
        activeParent?.parentId
      );
      if (
        parentCategory &&
        parentCategory?.categoryCode !== '__root_category__'
      ) {
        setActiveParent(parentCategory);
        setSelectedCategories([]);
        updateSelectedCategories([parentCategory]);
        deleteParams('categoryId');
      } else {
        setSelectedCategories([]);
        updateSelectedCategories([]);
        deleteParams('categoryId', 'pCategoryId');
        setActiveParent(null);
      }
    } else {
      setActiveParent(null);
      deleteParams('pCategoryId', 'categoryId');
    }
  };
  useEffect(() => {
    if (selectedCategories?.length === 0) {
      return;
    }
    updateSelectedCategories(selectedCategories);
  }, [selectedCategories]);

  useEffect(() => {
    const pCategoryId = searchParams.get('pCategoryId');
    const categoryId = searchParams.get('categoryId');

    // Case 1: Both present
    console.log('Both present');
    if (pCategoryId && categoryId) {
      const parent = findCategoryByCode(categories, pCategoryId);

      if (parent) {
        setActiveParent(parent);

        // Now search for categoryId within the parent's children (recursively if needed)
        const categoryIds = categoryId.split(',');
        const selectedChildren: CategoryTreeItem[] = [];

        // Helper to collect matching children
        const findMatchingChildren = (node: CategoryTreeItem) => {
          for (const child of node?.children || []) {
            if (categoryIds.includes(child.categoryCode)) {
              selectedChildren.push(child);
            }
            // Also check nested children
            findMatchingChildren(child);
          }
        };

        findMatchingChildren(parent);

        if (selectedChildren.length) {
          updateSelectedCategories(selectedChildren);
          setSelectedCategories(selectedChildren);
        }
      }

      return;
    }

    // Case 2: Only pCategoryId present → select parent
    console.log('Only pCategoryId present → select parent');
    if (pCategoryId) {
      const parent = findCategoryByCode(categories, pCategoryId);
      if (parent) {
        setActiveParent(parent);
        updateSelectedCategories([parent]);
      }
      return;
    }

    // Case 3: Only categoryId present → select category
    console.log('Only categoryId present → select category');
    if (categoryId) {
      const categoryIds = categoryId.split(',');
      const selected: CategoryTreeItem[] = [];

      const findMatchingCategories = (node: CategoryTreeItem) => {
        if (categoryIds.includes(node.categoryCode)) {
          selected.push(node);
        }
        for (const child of node?.children || []) {
          findMatchingCategories(child);
        }
      };

      findMatchingCategories(categories);

      if (selected.length) {
        updateSelectedCategories(selected);
        setSelectedCategories(selected);
      }
    }
  }, []);

  return (
    <div className="category-container">
      <div className="category-list">
        {!activeParent ? (
          <>
            <button
              className={`category-button primary-font-regular ${
                selectedCategories?.length === 0 ? 'selected' : ''
              }`}
              onClick={() => {
                setSelectedCategories([]);
                updateSelectedCategories([]);
                deleteParams('categoryId');
              }}
            >
              All
            </button>
            <RenderCategories
              categories={categories?.children || []}
              handleCategoryClick={handleCategoryClick}
              selectedCategories={selectedCategories}
            />
          </>
        ) : (
          <div className="active-category">
            <button
              className="parent-category primary-font-regular"
              onClick={() => {
                onClickBack();
              }}
            >
              <img src={SVGS.LeftArrowWhite} alt="" width={4} />{' '}
              {activeParent?.name}
            </button>
            <div className="category-separator" />
            <div className="child-categories">
              <button
                className={`category-button primary-font-regular ${
                  selectedCategories?.length === 0 ? 'selected' : ''
                }`}
                onClick={() => {
                  setSelectedCategories([]);
                  deleteParams('categoryId');
                  updateSelectedCategories([activeParent]);
                }}
              >
                All
              </button>
              <RenderCategories
                categories={activeParent?.children || []}
                handleCategoryClick={handleCategoryClick}
                selectedCategories={selectedCategories}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
